/**
 * Shared conflict resolution overlay for all editors.
 *
 * Wraps editor content and handles:
 *   - Orange "File changed externally" banner with toggle
 *   - Saved version overlay (fetched from disk, read-only)
 *   - Scroll sync between editor and overlay
 *   - Discard / Overwrite actions
 *
 * Usage:
 *   <ConflictOverlay
 *     isConflicted={isConflicted}
 *     conflictGeneration={conflictGeneration}
 *     fileId={fileId}
 *     onDiscard={discard}
 *     onOverwrite={forceSave}
 *     onConflictChange={onConflictChange}
 *     language="html"
 *     scrollRef={scrollRef}
 *     scrollSyncTrigger={scrollSyncTrigger}
 *   >
 *     {(conflictProps) => <YourEditorContent {...conflictProps} />}
 *   </ConflictOverlay>
 */

import { useState, useEffect, useRef, useCallback, type ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import { ConflictBanner, useSavedVersion, type ConflictView } from './ConflictBanner.js'
import { TextModifier, type TextModifierHandle } from './TextModifier.js'
import { useSavedVersionCover } from './useSavedVersionCover.js'

export interface ConflictOverlayProps {
  isConflicted: boolean
  conflictGeneration: number
  fileId: string
  onDiscard: () => void
  onOverwrite: () => void
  onConflictChange?: (conflicted: boolean) => void
  /** Language for syntax highlighting in the default text overlay */
  language?: string
  scrollRef?: Record<string, number>
  viewSync?: Record<string, Record<string, unknown>>
  viewSyncId?: string
  scrollSyncTrigger?: number
  /** Ref to the editor's TextModifier (for scroll sync editor→overlay) */
  editorTextareaRef?: React.RefObject<TextModifierHandle | null>
  /** Whether to skip rendering (e.g., previewMode) */
  disabled?: boolean
  /**
   * Custom renderer for the saved version overlay.
   * If provided, replaces the default TextModifier.
   * Receives the saved content (string) and loading state.
   * Use this for non-text editors (iframes, grids, Univer, etc.)
   */
  renderSavedVersion?: (content: string | null, loading: boolean) => ReactNode
  children: ReactNode
}

/** Props passed to the editor content when conflict is active */
export interface ConflictContentProps {
  /** True when conflict is active — editor should be read-only */
  conflictReadOnly: boolean
}

export function ConflictOverlay({
  isConflicted,
  conflictGeneration,
  fileId,
  onDiscard,
  onOverwrite,
  onConflictChange,
  language,
  scrollRef,
  viewSync,
  viewSyncId,
  scrollSyncTrigger,
  editorTextareaRef,
  disabled,
  renderSavedVersion,
  children,
}: ConflictOverlayProps) {
  const [conflictView, setConflictView] = useState<ConflictView>('mine')
  const [_conflictSyncTrigger, setConflictSyncTrigger] = useState(0)
  const overlayRef = useRef<TextModifierHandle>(null)

  const handleConflictViewChange = useCallback((view: ConflictView) => {
    if (view === 'mine') {
      setConflictSyncTrigger(n => n + 1)
    }
    setConflictView(view)
  }, [])

  // Reset to "Your changes" when conflict resolves
  useEffect(() => {
    if (!isConflicted) setConflictView('mine')
  }, [isConflicted])

  // Relay conflict state to parent
  useEffect(() => {
    onConflictChange?.(isConflicted)
  }, [isConflicted, onConflictChange])

  const showSavedVersion = isConflicted && conflictView === 'saved'
  // #930 — the saved copy is laid over the still-mounted live editor: report it as a cover (Save refused, the editor's
  // commands greyed). Same helper as the office editors.
  useSavedVersionCover(showSavedVersion, () => handleConflictViewChange('mine'))
  const savedVersion = useSavedVersion(fileId, isConflicted && conflictView === 'saved', conflictGeneration)

  // Sync overlay scroll to editor position on mount
  useEffect(() => {
    if (!showSavedVersion || savedVersion.loading) return
    const editorTa = editorTextareaRef?.current?.textarea
    const overlayTa = overlayRef.current?.textarea
    if (!editorTa || !overlayTa) return
    const pos = editorTa.scrollTop
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        overlayTa.scrollTop = pos
      })
    })
  }, [showSavedVersion, savedVersion.loading, editorTextareaRef])

  if (disabled) return <>{children}</>

  return (
    <>
      {/* Conflict banner */}
      {isConflicted && (
        <ConflictBanner
          fileId={fileId}
          view={conflictView}
          onViewChange={handleConflictViewChange}
          onDiscard={onDiscard}
          onOverwrite={onOverwrite}
        />
      )}

      {/* Editor content + saved version overlay */}
      <div className="flex-1 flex flex-col overflow-hidden relative min-h-0">
        {children}

        {/* Saved version overlay — covers editor, preserving scroll */}
        {showSavedVersion && (
          <div className="absolute inset-0 z-10 themed-bg-primary flex flex-col overflow-hidden">
            {renderSavedVersion ? (
              renderSavedVersion(savedVersion.content, savedVersion.loading)
            ) : savedVersion.loading ? (
              <div className="flex-1 flex items-center justify-center">
                <Loader2 className="animate-spin themed-text-muted" size={24} />
              </div>
            ) : (
              <TextModifier
                ref={overlayRef}
                value={savedVersion.content || ''}
                language={language}
                isActive={false}
                mode="readonly"
                scrollKey={fileId}
                scrollRef={scrollRef}
                viewSync={viewSync}
                viewSyncId={viewSyncId ? viewSyncId.replace(':edit', ':readonly') : undefined}
                scrollSyncTrigger={scrollSyncTrigger}
              />
            )}
          </div>
        )}
      </div>
    </>
  )
}
